import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import React from "react";

import { getStationName } from "../model/utils";

import { RebiddingTablePDF } from "./RebiddingTablePDF";

import type { OrderDetailResponseDTO } from "@/common/api";
import { registerPdfFonts } from "@/common/config/pdf";
import { getTariffLabel, formatDatetime } from "@/common/lib";
import { TARIFF_LABELS } from "@/common/model";

// Определяем, какой шрифт использовать
const fontFamily = registerPdfFonts();

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: fontFamily,
  },
  title: {
    fontSize: 18,
    marginBottom: 20,
    fontWeight: "bold",
    textAlign: "center",
  },
  section: {
    marginBottom: 20,
  },
  sectionLast: {
    marginBottom: 5,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 5,
    padding: 6,
  },
  blockTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
    marginTop: 10,
  },
  divider: {
    borderBottom: "1pt solid #000",
    marginTop: 10,
    marginBottom: 10,
  },
  row: {
    flexDirection: "row",
    marginBottom: 2,
    paddingVertical: 4,
  },
  periodRow: {
    flexDirection: "row",
    marginBottom: 1,
    paddingVertical: 2,
  },
  cell: {
    padding: 4,
  },
  boldCell: {
    fontWeight: "bold",
    padding: 4,
  },
  companyName: {
    fontSize: 12,
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    right: 30,
    fontSize: 8,
    color: "#666",
    marginTop: 30,
  },
});

interface TradeResultsPDFProps {
  order: OrderDetailResponseDTO;
  winnersCompany?: string[];
  directions?: any[];
  participants?: any[];
  getCompanyWinner: (company: string) => any;
  tableRebiddingData?: any[];
  tableParticipants?: any[];
  proposals?: Record<string, string>;
  maxRebiddingCount?: number;
  totalWagonsRequired?: string | number;
}

export const TradeResultsPDF: React.FC<TradeResultsPDFProps> = ({
  order,
  winnersCompany,
  directions,
  participants,
  getCompanyWinner,
  tableRebiddingData,
  tableParticipants,
  proposals,
  maxRebiddingCount = 0,
  totalWagonsRequired,
}) => {
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Text style={styles.title}>
          Протокол по заявке: №{order.orderNumber ?? order.id}{" "}
        </Text>
        <Text
          style={styles.footer}
          fixed
          render={({ pageNumber }) =>
            `Заявка ${order.orderNumber ?? order.id} Лист ${pageNumber}`
          }
        />

        {/* Блок 1: Период проведения */}
        <Text style={styles.blockTitle}>Блок 1</Text>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Период проведения:</Text>
          <View style={styles.periodRow}>
            <Text style={styles.cell}>
              <Text style={styles.boldCell}>Начало: </Text>
              {formatDatetime(order.tradeBegin) ?? "-"}
              {" "}
              <Text style={styles.boldCell}>Окончание: </Text>
              {formatDatetime(order.tradeEnd) ?? "-"}
            </Text>
          </View>
          <View style={styles.periodRow}>
            <Text style={styles.cell}>
              <Text style={styles.boldCell}>1 Этап Сбор предложений </Text>
              {formatDatetime(order.collectionBegin) ?? ""} – {formatDatetime(order.collectionEnd) ?? ""}
            </Text>
          </View>
          <View style={styles.periodRow}>
            <Text style={styles.cell}>
              <Text style={styles.boldCell}>2 Этап Переторжка </Text>
              {formatDatetime(order.retradingBegin) ?? ""} – {formatDatetime(order.retradingEnd) ?? ""}
            </Text>
          </View>
          <View style={styles.periodRow}>
            <Text style={styles.cell}>
              <Text style={styles.boldCell}>3 Этап Подведение итогов </Text>
              {formatDatetime(order.retradingEnd) ?? ""} – {formatDatetime(order.tradeEnd) ?? "-"}
            </Text>
          </View>
        </View>
        <View style={styles.divider} />

        {/* Блок 2: Участники */}
        <Text style={styles.blockTitle}>Блок 2</Text>
        {(() => {
          // Объединяем участников и победителей, исключая дубликаты
          const allParticipants = [...(participants || [])];
          const participantCompanyNames = new Set(
            (participants || []).map((p: any) => p.companyName),
          );

          // Добавляем победителей, если их еще нет в списке участников
          if (winnersCompany && winnersCompany.length > 0) {
            winnersCompany.forEach((company: string) => {
              if (!participantCompanyNames.has(company)) {
                allParticipants.push({ companyName: company });
              }
            });
          }

          return allParticipants.length > 0 ? (
            <View style={styles.sectionLast}>
              <View style={styles.periodRow}>
                <Text style={styles.cell}>
                  <Text style={styles.boldCell}>Участники: </Text>
                  {allParticipants.map((participant: any, index: number) => (
                    <Text key={index}>
                      {index === allParticipants.length - 1
                        ? participant.companyName
                        : `${participant.companyName}, `}
                    </Text>
                  ))}
                </Text>
              </View>
              <View style={styles.periodRow}>
                <Text style={styles.cell}>
                  <Text style={styles.boldCell}>Количество участников: </Text>
                  {allParticipants.length}
                </Text>
              </View>
            </View>
          ) : null;
        })()}
        <View style={styles.divider} />

        {/* Блок 3: Победители */}
        <Text style={styles.blockTitle}>Блок 3</Text>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Победители:</Text>
          {winnersCompany && winnersCompany.length > 0 ? (
            winnersCompany.map((company, index) => {
              const companyWinner = getCompanyWinner(company);
              return (
                <View key={index} style={{ marginLeft: 8 }}>
                  <View style={styles.periodRow}>
                    <Text style={styles.cell}>
                      <Text style={styles.boldCell}>{index + 1}) </Text>
                      {company}
                    </Text>
                  </View>
                  <View>
                    {directions?.map((item) => {
                      const offer = companyWinner?.offers?.find(
                        (offer: any) => offer.indexNumber === item.indexNumber,
                      );
                      const station =
                        getStationName(item.unloadingRoad, item.unloadingStation) ?? "-";
                      const tariff = item.tariffRoad ?? null;
                      const offerValue = offer?.wagonTariff ?? null;
                      const isCoefficient =
                        tariff != null && getTariffLabel(tariff) === TARIFF_LABELS.COEFFICIENT;
                      const offerDisplay =
                        offerValue != null && offerValue !== ""
                          ? `${offerValue} ${isCoefficient ? "коэф." : "руб."}`
                          : "-";

                      return (
                        <View key={item.indexNumber} style={styles.periodRow}>
                          <Text style={styles.cell}>
                            {station} - {offerDisplay}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                  <View style={styles.periodRow}>
                    <Text style={styles.cell}>
                      <Text style={styles.boldCell}>Предложено – </Text>
                      {companyWinner?.countWagonsOwner
                        ? `${companyWinner.countWagonsOwner} вагонов`
                        : "-"}
                      <Text style={styles.boldCell}>/Согласовано </Text>
                      {companyWinner?.numberAgreedWagons
                        ? `${companyWinner.numberAgreedWagons} вагонов`
                        : "-"}
                    </Text>
                  </View>
                </View>
              );
            })
          ) : (
            <View style={styles.periodRow}>
              <Text style={styles.cell}>Победители не были выбраны</Text>
            </View>
          )}
        </View>
      </Page>

      {/* Сводная таблица по переторжке - всегда на новой странице */}
      <RebiddingTablePDF
        tableRebiddingData={tableRebiddingData || []}
        tableParticipants={tableParticipants || []}
        proposals={proposals}
        maxRebiddingCount={maxRebiddingCount}
        totalWagonsRequired={totalWagonsRequired}
        orderNumber={order.orderNumber ?? order.id}
      />
    </Document>
  );
};
